import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, StatCard, ui, fmtDate,
} from '../components/adminUi';

const STATUS_TONE = {
  published: 'blue',
  active: 'amber',
  in_progress: 'amber',
  assigned: 'amber',
  completed: 'green',
  cancelled: 'red',
  canceled: 'red',
};
const STATUS_LABEL = {
  published: 'E\'lon qilingan',
  active: 'Faol',
  in_progress: 'Jarayonda',
  assigned: 'Biriktirilgan',
  completed: 'Yakunlangan',
  cancelled: 'Bekor qilingan',
  canceled: 'Bekor qilingan',
};

const POLL_MS = 15000;

function fmtRating(rating, count) {
  // rating null (usta hali baholanmagan) => "Reyting yo'q", 0 deb ko'rsatmaymiz.
  if (rating == null) return 'Reyting yo\'q';
  const c = count != null ? count : 0;
  return `★ ${rating}${c ? ` (${c})` : ''}`;
}

function fmtPrice(price) {
  if (price == null) return '—';
  try {
    return `${Number(price).toLocaleString('ru-RU')} so'm`;
  } catch {
    return `${price} so'm`;
  }
}

export default function OrdersPage() {
  const { auth } = useAuth();

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const timerRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      // Load both independently so one failing doesn't blank the whole page.
      const [statsRes, ordersRes] = await Promise.allSettled([
        apiFetch('/api/admin/orders/stats', auth.token),
        apiFetch('/api/admin/orders', auth.token),
      ]);

      let anyOk = false;
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        setStats(await statsRes.value.json());
        anyOk = true;
      }
      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        const data = await ordersRes.value.json();
        setOrders(Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []);
        anyOk = true;
      }
      setFetchError(anyOk ? '' : 'Ma\'lumotlarni yuklashda xatolik yuz berdi.');
      const now = new Date();
      setLastUpdated(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    } catch {
      setFetchError('Ma\'lumotlarni yuklashda xatolik yuz berdi.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  return (
    <AdminLayout>
      <PageHeader title="Buyurtmalar monitoringi" subtitle="Real vaqtli statistika va buyurtmalar ro'yxati">
        <span style={ui.pagerInfo}>
          {lastUpdated ? `Yangilandi: ${lastUpdated}` : ''}
        </span>
        <button style={ui.pagerBtn} onClick={() => load()}>↻ Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : (
        <>
          <div style={ui.statGrid}>
            <StatCard label="Jami buyurtmalar" value={num(stats?.total)} />
            <StatCard label="E'lon qilingan" value={num(stats?.published)} accent="#2563eb" />
            <StatCard label="Faol" value={num(stats?.active)} accent="#b45309" />
            <StatCard label="Yakunlangan" value={num(stats?.completed)} accent="#16a34a" />
            <StatCard label="Bekor qilingan" value={num(stats?.cancelled)} accent="#dc2626" />
          </div>

          <h2 style={ui.title}>Buyurtmalar</h2>
          <div style={{ height: '10px' }} />

          {orders.length === 0 ? (
            <Empty icon="📦" title="Hali buyurtma yo'q" hint="Yangi buyurtmalar bu yerda ko'rinadi." />
          ) : (
            <div style={ui.tableWrap}>
              <table style={ui.table}>
                <thead>
                  <tr>
                    <th style={{ ...ui.th, width: '48px' }}>#</th>
                    <th style={ui.th}>Tavsif</th>
                    <th style={ui.th}>Manzil</th>
                    <th style={ui.th}>Holat</th>
                    <th style={ui.th}>Byudjet</th>
                    <th style={ui.th}>Takliflar</th>
                    <th style={ui.th}>Yaratilgan</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const offers = Array.isArray(o.offers) ? o.offers : [];
                    return (
                    <tr key={o.id} style={ui.tr}>
                      <td style={{ ...ui.td, color: 'var(--text-faint)', verticalAlign: 'top' }}>{o.id}</td>
                      <td style={{ ...ui.td, maxWidth: '260px', verticalAlign: 'top' }}>
                        <span style={{ display: 'block', color: 'var(--text)' }}>
                          {o.description || '—'}
                        </span>
                      </td>
                      <td style={{ ...ui.td, verticalAlign: 'top' }}>{o.address || '—'}</td>
                      <td style={{ ...ui.td, verticalAlign: 'top' }}>
                        <Badge tone={STATUS_TONE[o.status] || 'gray'}>
                          {STATUS_LABEL[o.status] || o.status || '—'}
                        </Badge>
                      </td>
                      <td style={{ ...ui.td, verticalAlign: 'top' }}>{o.budget != null ? fmtPrice(o.budget) : '—'}</td>
                      <td style={{ ...ui.td, verticalAlign: 'top', minWidth: '260px' }}>
                        <OffersCell offers={offers} />
                      </td>
                      <td style={{ ...ui.td, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{fmtDate(o.createdAt, true)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

const OFFER_STATUS_LABEL = {
  pending: 'Kutilmoqda',
  accepted: 'Qabul qilindi',
  rejected: 'Rad etildi',
  cancelled: 'Bekor qilindi',
  canceled: 'Bekor qilindi',
};
const OFFER_STATUS_TONE = {
  pending: 'amber',
  accepted: 'green',
  rejected: 'red',
  cancelled: 'gray',
  canceled: 'gray',
};

// Bitta buyurtmaning ustalar takliflari: ism + narx + reyting. Bo'sh bo'lsa
// "Hali takliflar yo'q" — bo'sh massiv sahifani sindirmaydi.
function OffersCell({ offers }) {
  if (!Array.isArray(offers) || offers.length === 0) {
    return <span style={oStyle.empty}>Hali takliflar yo'q</span>;
  }
  return (
    <div style={oStyle.list}>
      {offers.map((f) => (
        <div key={f.id} style={oStyle.item}>
          <div style={oStyle.topRow}>
            <span style={oStyle.name}>{f.masterName || '—'}</span>
            <span style={oStyle.price}>{fmtPrice(f.price)}</span>
          </div>
          <div style={oStyle.metaRow}>
            <span style={oStyle.rating}>{fmtRating(f.rating, f.ratingCount)}</span>
            <Badge tone={OFFER_STATUS_TONE[f.status] || 'gray'}>
              {OFFER_STATUS_LABEL[f.status] || f.status || '—'}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

const oStyle = {
  empty: { color: 'var(--text-faint)', fontStyle: 'italic', fontSize: '13px' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  item: {
    border: '1px solid var(--border, #e5e7eb)',
    borderRadius: '8px',
    padding: '8px 10px',
    background: 'var(--surface, #fff)',
  },
  topRow: { display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline' },
  name: { fontWeight: 600, color: 'var(--text)' },
  price: { fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' },
  metaRow: { display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginTop: '4px' },
  rating: { fontSize: '13px', color: 'var(--text-muted, #6b7280)', whiteSpace: 'nowrap' },
};

function num(v) {
  return v === null || v === undefined ? 0 : v;
}
